import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Play, Pause, SkipBack, SkipForward, X, Repeat, Repeat1, WifiOff, Download } from 'lucide-react';
import { RECITERS } from '../../services/quranAPI';
import { REPEAT_MODES, SPEEDS } from '../../hooks/useFullAudioPlayer';

const NASKH = "'Amiri', 'Noto Naskh Arabic', serif";

// Format seconds → M:SS
const fmtTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

// Equalizer bars (shows when playing)
const EqBars = ({ playing }) => (
  <div className="flex items-end gap-0.5 h-4" style={{ width: 14 }}>
    {[1,2,3,4].map(i => (
      <div
        key={i}
        className={`w-0.5 rounded-full bg-amber-400 eq-bar-${i}`}
        style={{
          height: playing ? '100%' : '30%',
          animationPlayState: playing ? 'running' : 'paused',
          transition: 'height 0.2s',
        }}
      />
    ))}
  </div>
);

/**
 * QuranAudioBar — persistent full-featured audio player bar.
 * Fixed just above the bottom nav, slides up when audio starts.
 */
const QuranAudioBar = ({
  player,       // return value of useFullAudioPlayer
  chapterName,
  reciterId,
  onVerseClick, // optional: scroll to verse when tapped
}) => {
  const { isDarkMode, isRTL } = useTheme();
  const { t } = useTranslation();

  const {
    currentVerse, currentIdx, playing, loading, progress, duration, elapsed,
    repeatMode, speed, visible, exiting, isOffline,
    togglePlay, playPrev, playNext, seek,
    cycleRepeat, cycleSpeed, stop, totalVerses,
  } = player;

  if (!visible || !currentVerse) return null;

  const reciterName = RECITERS[reciterId]?.name || 'Mishary Alafasy';

  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;
  const repeatActive = repeatMode !== 'none';

  return (
    <div
      data-testid="quran-audio-bar"
      className={`fixed bottom-24 left-0 right-0 z-[150] px-3 ${
        exiting ? 'player-exit' : 'player-enter'
      }`}
    >
      <div className={`rounded-2xl overflow-hidden shadow-2xl border ${
        isDarkMode
          ? 'bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] border-amber-500/20'
          : 'bg-white border-gray-200 shadow-xl'
      }`}>
        {/* Progress bar — tappable */}
        <div
          className={`relative h-1.5 cursor-pointer ${isDarkMode ? 'bg-white/10' : 'bg-gray-200'}`}
          onClick={e => {
            const rect = e.currentTarget.getBoundingClientRect();
            seek((e.clientX - rect.left) / rect.width);
          }}
        >
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-amber-500 transition-all"
            style={{ width: `${(progress * 100).toFixed(1)}%` }}
          />
        </div>

      <div className="px-4 py-2.5" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Row 1: Info + time + close */}
          <div className={`flex items-center gap-2 mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <EqBars playing={playing} />

            <div className="flex-1 min-w-0">
              <button
                onClick={() => onVerseClick?.(currentVerse.verse_key)}
                className={`block w-full text-left`}
              >
                <p
                  className={`text-sm font-semibold leading-tight truncate ${isDarkMode ? 'text-amber-300' : 'text-[var(--p-hex)]'}`}
                  style={{ fontFamily: NASKH, direction: 'rtl' }}
                >
                  {currentVerse.text_uthmani?.slice(0, 40)}…
                </p>
                <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} dir="ltr">
                  {chapterName} · {currentVerse.verse_key}
                  {totalVerses > 0 && ` (${(currentIdx || 0) + 1}/${totalVerses})`}
                  {' · '}{reciterName}
                </p>
              </button>
            </div>

            {/* Time + offline indicator */}
            <div className={`flex items-center gap-1.5 flex-shrink-0`}>
              {isOffline && (
                <span data-testid="audio-bar-offline-badge" className={`flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                  isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                }`}>
                  <WifiOff className="w-2.5 h-2.5" />
                  Offline
                </span>
              )}
              <span className={`text-[10px] tabular-nums ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} dir="ltr">
                {fmtTime(elapsed)}/{fmtTime(duration)}
              </span>
            </div>

            {/* Close — large, elder-friendly */}
            <button
              data-testid="audio-bar-close"
              onClick={stop}
              className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 transition-colors active:scale-95 ${
                isDarkMode ? 'bg-white/12 text-gray-300 hover:bg-white/20 hover:text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
              }`}
              aria-label="Close player"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Row 2: Controls */}
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            {/* Repeat mode — shows badge for 'all' to distinguish from off */}
            <button
              data-testid="repeat-btn"
              onClick={cycleRepeat}
              className={`relative w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                repeatActive
                  ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                  : isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-white/8' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              {repeatMode === 'one' ? <Repeat1 className="w-4 h-4" /> : <Repeat className="w-4 h-4" />}
              {repeatMode === 'all' && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-[#0f172a] text-[8px] font-black flex items-center justify-center">∞</span>
              )}
            </button>

            {/* Prev verse */}
            <button
              data-testid="prev-verse-btn"
              onClick={playPrev}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/8' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <SkipBack className="w-4 h-4" />
            </button>

            {/* Main Play/Pause */}
            <button
              data-testid="main-play-btn"
              onClick={togglePlay}
              className="w-11 h-11 rounded-2xl bg-amber-500 hover:bg-amber-400 flex items-center justify-center transition-all active:scale-95 shadow-md"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-[#0f172a] border-t-transparent rounded-full animate-spin" />
              ) : playing ? (
                <Pause className="w-5 h-5 text-[#0f172a]" />
              ) : (
                <Play className="w-5 h-5 text-[#0f172a] ml-0.5" />
              )}
            </button>

            {/* Next verse */}
            <button
              data-testid="next-verse-btn"
              onClick={playNext}
              className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${
                isDarkMode ? 'text-gray-400 hover:text-white hover:bg-white/8' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <SkipForward className="w-4 h-4" />
            </button>

            {/* Speed */}
            <button
              data-testid="speed-btn"
              onClick={cycleSpeed}
              className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold transition-colors ${
                speed !== 1
                  ? isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-600'
                  : isDarkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-white/8' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
            >
              {speed}×
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuranAudioBar;
